import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Check, Loader2, Ticket, Mail, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { GoldRule, Tassel } from "./ornaments";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-gold/25 bg-navy/60 px-4 py-3.5 text-sm text-ivory placeholder:text-mist/50 outline-none transition-all duration-300 focus:border-gold/70 focus:bg-navy/80 focus:shadow-[0_0_0_3px_rgba(212,175,55,0.12)]";

export const Rsvp = () => {
  const [status, setStatus] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [bringingGuest, setBringingGuest] = useState(false);
  const [wantsStream, setWantsStream] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/rsvp/status`);
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/rsvp`, {
        name, email, party_size: bringingGuest ? 2 : 1, wants_stream: wantsStream, note,
      });
      setConfirmation(data);
      toast.success("You're on the list — on se voit le 5 décembre !", { duration: 5000 });
      fetchStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong — please try again.";
      toast.error(detail, { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  const downloadIcs = () => {
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Graduation Celebration//EN",
      "BEGIN:VEVENT",
      `UID:${confirmation.ticket_code}@graduation-celebration`,
      "DTSTAMP:20261205T000000Z",
      "DTSTART:20261205T170000",
      "DTEND:20261205T200000",
      "SUMMARY:Graduation Celebration — Dr. Stephanie Darilus",
      "LOCATION:Sarasota\\, Florida",
      "DESCRIPTION:Celebration in honour of Dr. Stephanie Darilus on Saturday, December 5 at 5:00 PM. The exact address is shared by email after RSVP.",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "graduation-celebration.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  const soldOut = status && status.seats_remaining <= 0;

  return (
    <section id="rsvp" className="relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="rsvp-section">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-[-10%] top-[20%] h-[60vh] w-[40vw] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.1),transparent_65%)]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Fêtons ensemble</p>
          <h2 className="mt-4 font-serif text-4xl font-medium leading-[1.1] text-ivory sm:text-5xl lg:text-6xl">
            RSVP — <span className="text-gold-foil italic">join the celebration</span>
          </h2>
          <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-mist sm:text-base">
            Saturday, December 5 · Sarasota, Florida. RSVP below and we'll email you the
            address — and if you'd like to watch the December 15 ceremony live, just tick
            the box.
          </p>

          <ul className="mt-8 space-y-3 text-sm font-light text-mist">
            {[
              "Bringing a guest? Tick the box — we'd love to meet them",
              "We'll email you the address once you RSVP",
              "Want the ceremony live? We'll send you the link",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <Sparkles size={14} className="mt-0.5 shrink-0 text-gold" /> {line}
              </li>
            ))}
          </ul>

          <div className="pointer-events-none absolute right-8 top-24 hidden xl:block" aria-hidden="true" />
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative">
            <div className="pointer-events-none absolute -right-3 -top-16 hidden lg:block opacity-70" aria-hidden="true">
              <div className="animate-sway" style={{ transformOrigin: "top center" }}>
                <Tassel className="h-40 w-auto" />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {confirmation ? (
                <motion.div
                  key="ticket"
                  data-testid="rsvp-confirmation"
                  initial={{ opacity: 0, scale: 0.92, rotate: -3 }}
                  animate={{ opacity: 1, scale: 1, rotate: -1.5 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gold-pale via-gold to-gold-deep p-[3px] shadow-[0_30px_80px_rgba(212,175,55,0.25)]"
                >
                  <div className="relative rounded-[14px] bg-navy px-8 py-10 text-center">
                    <div className="absolute inset-x-8 top-1/2 h-px border-t border-dashed border-gold/30" aria-hidden="true" />
                    <Ticket className="mx-auto text-gold" size={40} />
                    <h3 className="mt-4 font-serif text-3xl text-ivory">You're on the list!</h3>
                    <p className="mt-2 font-script text-2xl text-gold-light">on se voit le 5 décembre !</p>
                    <div className="mt-8 inline-block rounded-xl border border-gold/40 bg-navy-deep px-8 py-4" data-testid="ticket-code">
                      <p className="text-[9px] uppercase tracking-[0.35em] text-mist">Your ticket code</p>
                      <p className="text-gold-foil mt-1 font-serif text-4xl font-semibold tracking-[0.15em]">{confirmation.ticket_code}</p>
                    </div>
                    <div className="mt-8 space-y-2 text-sm font-light text-mist">
                      <p className="flex items-center justify-center gap-2"><Users size={14} className="text-gold" /> {bringingGuest ? "You and your guest" : "Just you"} — party of {confirmation.party_size}</p>
                      <p className="flex items-center justify-center gap-2"><Mail size={14} className="text-gold" /> We'll email you the address{confirmation.wants_stream ? ", your livestream link" : ""} & all the details</p>
                    </div>
                    <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                      <button
                        data-testid="rsvp-calendar-button"
                        onClick={downloadIcs}
                        className="rounded-full border border-gold/40 px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-light transition-all duration-300 hover:bg-gold/10"
                      >
                        Add to calendar
                      </button>
                      <button
                        data-testid="rsvp-another-button"
                        onClick={() => { setConfirmation(null); setName(""); setEmail(""); setNote(""); setBringingGuest(false); setWantsStream(false); }}
                        className="rounded-full border border-gold/40 px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-light transition-all duration-300 hover:bg-gold/10"
                      >
                        Add another RSVP
                      </button>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="rsvp-form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="glass-gold relative rounded-3xl p-7 sm:p-10"
                >
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="rsvp-name" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">Full name *</label>
                      <input id="rsvp-name" data-testid="rsvp-name-input" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="rsvp-email" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">Email *</label>
                      <input id="rsvp-email" data-testid="rsvp-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                    </div>
                    <button
                      type="button"
                      data-testid="rsvp-guest-checkbox"
                      onClick={() => setBringingGuest((v) => !v)}
                      className={`flex w-full items-start gap-3 rounded-xl border px-4 py-4 text-left transition-all duration-300 ${bringingGuest ? "border-gold/70 bg-gold/10" : "border-gold/25 bg-navy/60 hover:border-gold/45"}`}
                      aria-pressed={bringingGuest}
                    >
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all duration-300 ${bringingGuest ? "border-gold bg-gold text-navy-deep" : "border-gold/40"}`}>
                        {bringingGuest && <Check size={13} strokeWidth={3} />}
                      </span>
                      <span className="text-xs font-light leading-relaxed text-ivory/90 sm:text-sm">
                        Yes — I'm bringing a guest
                      </span>
                    </button>
                    <button
                      type="button"
                      data-testid="rsvp-stream-checkbox"
                      onClick={() => setWantsStream((v) => !v)}
                      className={`flex w-full items-start gap-3 rounded-xl border px-4 py-4 text-left transition-all duration-300 ${wantsStream ? "border-gold/70 bg-gold/10" : "border-gold/25 bg-navy/60 hover:border-gold/45"}`}
                      aria-pressed={wantsStream}
                    >
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all duration-300 ${wantsStream ? "border-gold bg-gold text-navy-deep" : "border-gold/40"}`}>
                        {wantsStream && <Check size={13} strokeWidth={3} />}
                      </span>
                      <span className="text-xs font-light leading-relaxed text-ivory/90 sm:text-sm">
                        Yes — email me the private link to watch the commencement livestream on December 15
                      </span>
                    </button>
                    <div>
                      <label htmlFor="rsvp-note" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">A note for Stephanie <span className="normal-case tracking-normal text-mist/60">(optional)</span></label>
                      <textarea id="rsvp-note" data-testid="rsvp-note-input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Words of congratulations…" className={`${inputCls} resize-none`} />
                    </div>
                    <button
                      type="submit"
                      data-testid="rsvp-submit-button"
                      disabled={submitting || soldOut}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold-pale via-gold to-gold-deep py-4 text-xs font-semibold uppercase tracking-[0.25em] text-navy-deep transition-all duration-300 hover:shadow-[0_0_40px_rgba(212,175,55,0.35)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-none"
                    >
                      {submitting ? <Loader2 size={16} className="animate-spin" /> : <Ticket size={16} />}
                      {soldOut ? "Sold out" : submitting ? "Sending…" : "Send my RSVP"}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>

      <div className="relative mx-auto mt-20 max-w-7xl px-5 sm:px-8">
        <GoldRule className="justify-center" />
      </div>
    </section>
  );
};
